import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismIcon,
  PrismInput,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { not, or } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import Countries from 'frontend-utils/utils/countries';
import hasUnifiedSettingsActiveFeature from 'lumen/helpers/has-unified-settings-active-feature';
import type { SubscriptionProfile } from 'lumen/models';
import initGoogleMap from 'lumen/modifiers/init-google-map';
import { GENERAL_PROPERTY_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';

import styles from './card-address.module.css';

interface SettingsMyHotelCardAddressSignature {
  Args: {
    isLoading: boolean;
    profile: SubscriptionProfile | null | undefined;
  };
}

export default class SettingsMyHotelCardAddress extends Component<SettingsMyHotelCardAddressSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: IntlService;
  @service declare toast: Services['toast'];

  @tracked hasAttemptedSave = false;
  @tracked isSatelliteView = false;
  private countries: Countries = new Countries(getOwner(this)!);
  private mapInstance: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private static readonly OWNED_ATTRS = [
    'address',
    'latitude',
    'longitude',
  ] as const;

  repositionMarkerTask = task(
    { restartable: true },
    async (): Promise<void> => {
      if (!this.mapInstance || !this.canShowMap) {
        return;
      }
      await this.repositionMarker(this.mapInstance);
    },
  );

  saveTask = task({ drop: true }, async (): Promise<void> => {
    // UNIFIED SETTINGS TODO: remove once api is integrated
    await timeout(500);

    this.hasAttemptedSave = true;

    if (!this.hasChanges) {
      return;
    }

    if (this.hasValidationErrors) {
      return;
    }

    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.address = {
      ...profile.address,
      street: profile.address.street.trim(),
      number: profile.address.number.trim(),
      postalCode: profile.address.postalCode.trim(),
      cityName: profile.address.cityName.trim(),
    };

    try {
      await profile.saveForSubscription(profile.id!);
      this.toast.success(
        this.intl.t('settings.my_hotel.hotel_details.save_success'),
      );
      this.hasAttemptedSave = false;
      this.mapInstance?.setZoom(15);
    } catch {
      await this.alerting.showErrorAlert();
    }
  });

  get canShowMap(): boolean {
    const { profile } = this.args;
    if (!profile) {
      return false;
    }

    if (profile.latitude !== null && profile.longitude !== null) {
      return true;
    }

    const a = profile.address;

    return (
      (a?.street?.trim() ?? '') !== '' &&
      (a?.cityName?.trim() ?? '') !== '' &&
      (a?.country?.trim() ?? '') !== ''
    );
  }

  @cached
  get countryOptions(): { id: string; label: string }[] {
    return this.countries.list.map((c) => ({ id: c.id, label: c.name }));
  }

  get hasChanges(): boolean {
    return (
      this.args.profile?.hasValuesChanges(
        SettingsMyHotelCardAddress.OWNED_ATTRS,
      ) ?? false
    );
  }

  get hasValidationErrors(): boolean {
    return !this.isStreetValid || !this.isCityValid || !this.isCountryValid;
  }

  get isCityValid(): boolean {
    return (this.args.profile?.address?.cityName ?? '').trim() !== '';
  }

  get isCountryValid(): boolean {
    return (this.args.profile?.address?.country ?? '').trim() !== '';
  }

  get isStreetValid(): boolean {
    return (this.args.profile?.address?.street ?? '').trim() !== '';
  }

  @cached
  get selectedCountry(): { id: string; label: string } | undefined {
    return this.countryOptions.find(
      (c) => c.id === this.args.profile?.address?.country,
    );
  }

  get showCountryError(): boolean {
    return this.hasAttemptedSave && !this.isCountryValid;
  }

  private clearStoredCoords(): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }
    if (profile.latitude !== null || profile.longitude !== null) {
      profile.latitude = null;
      profile.longitude = null;
    }
  }

  private async geocodeAddress(): Promise<google.maps.GeocoderResult | null> {
    const address = this.args.profile?.address;
    const { street, number, postalCode, cityName, country } = address ?? {};
    const geocoder = new google.maps.Geocoder();

    try {
      const result = await geocoder.geocode({
        address: [street, number, postalCode, cityName]
          .filter(Boolean)
          .join(' '),
        componentRestrictions: { country },
      });

      return result.results[0] ?? null;
    } catch {
      // UNIFIED SETTINGS TODO: add monitoring later
      return null;
    }
  }

  private async geocodeAndRenderMap(map: google.maps.Map): Promise<void> {
    const result = await this.geocodeAddress();

    if (result) {
      const location = result.geometry.location;
      const lat = location.lat();
      const lng = location.lng();

      if (this.args.profile) {
        this.args.profile.latitude = lat;
        this.args.profile.longitude = lng;
      }

      this.renderMapWithCoords(map, lat, lng);
    }
  }

  private renderMapWithCoords(
    map: google.maps.Map,
    lat: number,
    lng: number,
  ): void {
    const position = { lat, lng };

    if (this.marker) {
      this.marker.setPosition(position);
      map.panTo(position);
    } else {
      map.setCenter(position);
      map.setZoom(15);
      this.marker = new google.maps.Marker({ map, position, draggable: true });
      this.marker.addListener('dragend', () => {
        const pos = this.marker!.getPosition();

        if (pos && this.args.profile) {
          this.args.profile.latitude = pos.lat();
          this.args.profile.longitude = pos.lng();
        }
      });
    }
  }

  private async repositionMarker(map: google.maps.Map): Promise<void> {
    const { profile } = this.args;
    if (
      profile?.latitude !== null &&
      profile?.latitude !== undefined &&
      profile?.longitude !== null &&
      profile?.longitude !== undefined
    ) {
      this.renderMapWithCoords(map, profile.latitude, profile.longitude);
    } else {
      await this.geocodeAndRenderMap(map);
    }
  }

  searchMatcher(option: Option & { id: string }, searchText: string): number {
    const s = searchText.toLowerCase();
    return option.label.toLowerCase().includes(s) ||
      option.id.toLowerCase().includes(s)
      ? 1
      : -1;
  }

  @action async cancel(): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.keep_editing'),
        },
        confirm: {
          text: this.intl.t('general.discard_changes'),
        },
      },
    });

    if (confirmed) {
      this.marker?.setMap(null);
      this.marker = null;

      const { profile } = this.args;
      if (profile) {
        const changed = profile.changedAttributes();
        for (const attr of SettingsMyHotelCardAddress.OWNED_ATTRS) {
          if (attr in changed) {
            (profile as unknown as Record<string, unknown>)[attr] =
              changed[attr]![0];
          }
        }
      }

      this.hasAttemptedSave = false;
      void this.repositionMarkerTask.perform();
    }
  }

  @action handleAddressBlur(): void {
    void this.repositionMarkerTask.perform();
  }

  @action handleMapReady(map: google.maps.Map): void {
    if (this.mapInstance !== map) {
      this.marker = null;
    }

    this.mapInstance = map;
    map.setOptions({ scrollwheel: true, gestureHandling: 'greedy' });

    void this.repositionMarker(map);
  }

  @action setAddressField(
    field: 'cityName' | 'number' | 'postalCode' | 'street',
    event: Event,
  ): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.address = {
      ...profile.address,
      [field]: (event.target as HTMLInputElement).value,
    };

    this.clearStoredCoords();
  }

  @action toggleSatelliteView(): void {
    this.isSatelliteView = !this.isSatelliteView;
    this.mapInstance?.setMapTypeId(this.isSatelliteView ? 'hybrid' : 'roadmap');
  }

  @action updateCountry(option: { id: string; label: string }): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.address = { ...profile.address, country: option.id };
    this.clearStoredCoords();
    void this.repositionMarkerTask.perform();
  }

  <template>
    <PrismCard data-test-id="card-address">
      <:header as |Header|>
        <Header.title>
          {{t "settings.my_hotel.hotel_details.address"}}
        </Header.title>
      </:header>

      <:content>
        <div class="grid grid-cols-3 gap-400 pt-400">
          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_ADDRESS
            )
          }}
            <div class="col-span-2 flex">
              <PrismInput
                @isTouched={{this.hasAttemptedSave}}
                @label={{t "settings.my_hotel.hotel_details.street"}}
                @onBlur={{this.handleAddressBlur}}
                @onInput={{fn this.setAddressField "street"}}
                @removePadding={{true}}
                @required={{true}}
                @requiredErrorMessage={{t "general.required"}}
                @value={{@profile.address.street}}
                data-test-id="input-street"
              />
            </div>

            <div class="flex self-start">
              <PrismInput
                @label={{t "settings.my_hotel.hotel_details.number"}}
                @onBlur={{this.handleAddressBlur}}
                @onInput={{fn this.setAddressField "number"}}
                @removePadding={{true}}
                @value={{@profile.address.number}}
                data-test-id="input-number"
              />
            </div>

            <div class="flex self-start">
              <PrismInput
                @label={{t "settings.my_hotel.hotel_details.postal_code"}}
                @onBlur={{this.handleAddressBlur}}
                @onInput={{fn this.setAddressField "postalCode"}}
                @removePadding={{true}}
                @value={{@profile.address.postalCode}}
                data-test-id="input-postal-code"
              />
            </div>

            <div class="flex self-start">
              <PrismInput
                @isTouched={{this.hasAttemptedSave}}
                @label={{t "settings.my_hotel.hotel_details.city"}}
                @onBlur={{this.handleAddressBlur}}
                @onInput={{fn this.setAddressField "cityName"}}
                @removePadding={{true}}
                @required={{true}}
                @requiredErrorMessage={{t "general.required"}}
                @value={{@profile.address.cityName}}
                data-test-id="input-city"
              />
            </div>

            <div class="grid self-start">
              <label class="text-200--bold mb-100 flex gap-50">
                {{t "settings.my_hotel.hotel_details.country"}}
                <span class="text-neutral-default font-bold"> *</span>
              </label>
              <PrismComboSelectInput
                @isSearchable={{true}}
                @isSecondary={{true}}
                @onChange={{this.updateCountry}}
                @options={{this.countryOptions}}
                @searchMatcher={{this.searchMatcher}}
                @value={{this.selectedCountry}}
                class={{local
                  styles
                  (if this.showCountryError "country-select--error")
                }}
                data-test-id="select-country"
              />
              {{! Same pattern used in modal-copy-to-existing }}
              {{#if this.showCountryError}}
                <div
                  class="flex items-center gap-100"
                  data-test-id="error-country"
                >
                  <PrismIcon
                    @color="critical"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  <p class="text-critical-default text-200--medium">
                    {{t "general.required"}}
                  </p>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_COORDINATES
            )
          }}
            {{#if this.canShowMap}}
              <div class="col-span-3">
                <div class="mb-100 flex items-center justify-between">
                  <p class="text-200--regular" data-test-id="map-instructions">
                    {{t
                      "settings.my_hotel.hotel_details.map_drag_instructions"
                    }}
                  </p>
                  <PrismToggle
                    @dataTestId="toggle-satellite-view"
                    @isChecked={{this.isSatelliteView}}
                    @label={{t
                      "settings.my_hotel.hotel_details.satellite_view"
                    }}
                    @onChange={{this.toggleSatelliteView}}
                  />
                </div>
                <div
                  class="rounded-100 {{styles.map}}"
                  data-test-id="hotel-details-map"
                  {{initGoogleMap this.handleMapReady}}
                ></div>
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:content>

      <:footer>
        <div class="flex justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{or (not this.hasChanges) this.saveTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.cancel}}
            data-test-id="button-cancel-address"
          />
          <PrismButton
            @isDisabled={{not this.hasChanges}}
            @isLoading={{or @isLoading this.saveTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{this.saveTask.perform}}
            data-test-id="button-save-address"
          />
        </div>
      </:footer>
    </PrismCard>
  </template>
}
